import { dedupeConsecutiveWhitespace } from '@src/components/transcript/utils';
import { softBreakLineEndIds } from '@src/editor/services/captionSoftBreaks';
import { useKaraokeStore, useTranscription } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useMemo } from 'react';

import type { TranscriptElementWithViewData } from '@src/types.ts/transcript';

/**
 * Derives display-ready transcript elements with karaoke-specific mutations (muted state for hidden tokens)
 * while preserving structural sharing for performance on large arrays (up to ~30k elements).
 *
 * Rules:
 * - If in translated subtitles view: return subtitleElements directly.
 * - If in karaoke view: flatten karaoke lines as before (including viewData + isHidden + highlight info).
 * - Outside karaoke view: mark any tokens that are hidden in karaoke lines as state='muted'.
 *   Only allocate new objects for changed elements.
 */
export function useDisplayTranscriptElements(
  isInKaraokeView: boolean,
): TranscriptElementWithViewData[] | undefined {
  const transcriptionStore = useTranscription();
  const karaokeStore = useKaraokeStore();
  const videoCreator = useVideoCreatorStore();

  const base = transcriptionStore.getFinalTranscriptionElements();
  const karaokeLines = karaokeStore.getKaraokeLinesFormatted();
  const punchListSignature = videoCreator.punchListManager.punchListSignature;

  return useMemo(() => {
    if (!base) return base;

    // Karaoke view flatten logic (retain existing behavior)
    if (isInKaraokeView && karaokeLines) {
      // Flatten karaoke lines, carrying through original transcript index
      // mapping for downstream consumers
      const indexById = new Map<string, number>();
      for (let i = 0; i < base.length; i++) {
        const el = base[i];
        if (el.id) indexById.set(el.id, i);
      }
      const flattened = karaokeLines.flatMap(line => {
        // Draw each break where the line really ends: past punctuation added
        // after the token it was stored on, as the renderer does.
        const softBreakIds = softBreakLineEndIds(
          line.transcriptTokens,
          line.softBreakAfterTokenIds ?? [],
        );
        return line.transcriptTokens.map((token, tokenIndex) => {
          const isHidden = line.allHiddenTranscriptTokenIds.includes(token.id);
          const isLastInLine = tokenIndex === line.transcriptTokens.length - 1;
          const transcriptIndex = indexById.get(token.id);
          return {
            ...token,
            state: isHidden ? 'muted' : token.state,
            viewData: {
              finalTranscriptIndex: transcriptIndex,
              isLastInLine,
              isHidden,
              lineId: line.id,
              softBreakAfter: softBreakIds.has(token.id),
              ...(transcriptIndex != null
                ? videoCreator.punchListManager.getPhotoHighlightViewData(
                    transcriptIndex,
                  )
                : {
                    punchListItem: null,
                    lastPhotoHighlightElementId: null,
                    lastPhotoHighlightActive: false,
                  }),
            },
          };
        });
      });
      // The flattened karaoke tokens carry the fields consumers read as
      // display elements; the cast bridges TranscriptToken -> element view
      // shape (previously masked by the now-removed subtitleElements `any`).
      return dedupeConsecutiveWhitespace(
        flattened,
      ) as unknown as TranscriptElementWithViewData[];
    }

    // Outside karaoke view: augment base with muted state for hidden tokens (if any)
    // Aggregate hidden ids once.
    let hiddenIds: string[] = [];
    if (karaokeLines) {
      for (const line of karaokeLines) {
        if (line.allHiddenTranscriptTokenIds.length) {
          hiddenIds.push(...line.allHiddenTranscriptTokenIds);
        }
      }
    }
    const hiddenSet = new Set(hiddenIds);

    // Detect if any element actually needs a `state: 'muted'` flag.
    let needsChange = false;
    for (const el of base) {
      if (el.id && hiddenSet.has(el.id) && el.state !== 'muted') {
        needsChange = true;
        break;
      }
    }

    // Defense-in-depth dedupe: even with the punctuation.ts fillIn fix
    // and an empty hidden set, an active transcript reconstructed from
    // cut clips can carry consecutive whitespace tokens at segment
    // boundaries. Run the same dedupe pass we use for the karaoke
    // branch so the no-consecutive-whitespace invariant holds for every
    // consumer of `displayElements`.
    if (!needsChange) {
      return dedupeConsecutiveWhitespace(base);
    }

    const out: TranscriptElementWithViewData[] = new Array(base.length);
    for (let i = 0; i < base.length; i++) {
      const el = base[i];
      if (el.id && hiddenSet.has(el.id) && el.state !== 'muted') {
        out[i] = { ...el, state: 'muted' };
      } else {
        out[i] = el;
      }
    }
    return dedupeConsecutiveWhitespace(out);
  }, [
    base,
    isInKaraokeView,
    karaokeLines,
    punchListSignature, // highlight changes should invalidate karaoke path
  ]);
}
